import { Home as HomeIcon } from "@mui/icons-material";
import { Box, Popper, Typography } from "@mui/material";
import { LayersFill } from "@procore/core-icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButton } from "components/toolbars";
import {
    QUICK_ACCESS_TOOLBAR,
    TOOLBAR_BUTTON,
    TOOLBAR_COLORS,
    TOOLBAR_CONTAINER,
    TOOLBAR_Z_INDEX,
} from "components/toolbars/constants";
import { ToolbarLayerProvider } from "components/toolbars/ToolbarLayerProvider";
import { useFlyoutRegistration } from "components/toolbars/useFlyoutRegistration";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    CORE_TOOLBAR_IDS,
    ToolbarButtonEntry,
    toolbarsActions,
    useDispatchToolbars,
    useToolbarButtons,
} from "contexts/toolbars";
import { useResetView } from "features/home/useResetView";
import { getOrthoFitClipPlane, getOrthoFromPinhole } from "features/orthoCam/utils";
import {
    CameraType,
    renderActions,
    selectCameraDefaults,
    selectCameraType,
    selectIsSubtreesPanelOpen,
    selectIsXRayActive,
    selectMainObject,
    selectMainObjectPick,
} from "features/render";
import { useSetController } from "features/render/hooks/useSetController";
import { useToggleXRayMode } from "features/render/hooks/useToggleXRayMode";
import { SubtreesPopover } from "features/render/subtreesPopover";
import { useSpaceMouseAccess } from "features/spaceMouse";
import CursorIcon from "media/icons/cursor.svg?react";
import OrbitIcon from "media/icons/orbit.svg?react";
import OrthographicIcon from "media/icons/orthographic.svg?react";
import PerspectiveIcon from "media/icons/perspective.svg?react";
import SpaceMouseIcon from "media/icons/spacemouse.svg?react";
import WalkIcon from "media/icons/walk.svg?react";
import XRayIcon from "media/icons/xray.svg?react";
import { telemetry } from "telemetry";
import { isMacintosh } from "utils/misc";

type ControllerValue = "flight" | "hover" | "cadMiddlePan";
type NavMode = {
    id: ControllerValue;
    Icon: React.ComponentType;
    label: string;
    shortcutKey: string;
};

function defaultNavIcon(spaceMouseEnabled: boolean) {
    return spaceMouseEnabled ? SpaceMouseIcon : CursorIcon;
}

const NAV_MODES: NavMode[] = [
    { id: "flight", Icon: CursorIcon, label: "Default", shortcutKey: "V" },
    { id: "cadMiddlePan", Icon: OrbitIcon, label: "Orbit", shortcutKey: "O" },
    { id: "hover", Icon: WalkIcon, label: "Walk", shortcutKey: "F" },
];

const SPACEMOUSE_NAV_MODES: NavMode[] = [{ ...NAV_MODES[0], Icon: SpaceMouseIcon }, NAV_MODES[1], NAV_MODES[2]];

function NavModeIcon({ controller, spaceMouseEnabled }: { controller: string; spaceMouseEnabled: boolean }) {
    if (controller === "flight") {
        const Icon = defaultNavIcon(spaceMouseEnabled);
        return <Icon />;
    }
    if (controller === "cadMiddlePan") {
        return <OrbitIcon />;
    }
    return <WalkIcon />;
}

function navModes(spaceMouseEnabled: boolean): NavMode[] {
    return spaceMouseEnabled ? SPACEMOUSE_NAV_MODES : NAV_MODES;
}

// 6px gap positions the flyout 2px above the toolbar top edge
const FLYOUT_OFFSET: [number, number] = [0, 6];

const containerStyle = {
    display: "flex",
    alignItems: "center",
    gap: `${TOOLBAR_CONTAINER.GAP}px`,
    padding: `${TOOLBAR_CONTAINER.PADDING}px 12px`,
    backgroundColor: TOOLBAR_CONTAINER.BG,
    borderRadius: "48px",
    border: TOOLBAR_CONTAINER.BORDER,
    boxShadow: TOOLBAR_CONTAINER.SHADOW,
    pointerEvents: "auto" as const,
} as const;

type NavModesPopoverProps = {
    open: boolean;
    anchorEl: HTMLButtonElement | null;
    flyoutRef: React.RefObject<HTMLDivElement>;
    controller: string;
    spaceMouseEnabled: boolean;
    setController: (id: ControllerValue) => void;
    onClose: () => void;
};

function NavModesPopover({
    open,
    anchorEl,
    flyoutRef,
    controller,
    spaceMouseEnabled,
    setController,
    onClose,
}: NavModesPopoverProps) {
    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="top-start"
            sx={{ zIndex: QUICK_ACCESS_TOOLBAR.FLYOUT_Z_INDEX }}
            modifiers={[{ name: "offset", options: { offset: FLYOUT_OFFSET } }]}
        >
            <div ref={flyoutRef}>
                <Box sx={flyoutContainerStyle}>
                    {navModes(spaceMouseEnabled).map(({ id, Icon, label, shortcutKey }) => {
                        const isActive = controller === id;
                        return (
                            <Box
                                key={id}
                                role="button"
                                onClick={() => {
                                    setController(id);
                                    onClose();
                                    const event =
                                        id === "flight"
                                            ? Events.toolbar.navigationDefaultTapped
                                            : id === "cadMiddlePan"
                                              ? Events.toolbar.navigationOrbitTapped
                                              : Events.toolbar.navigationFlyTapped;
                                    void track(event, { origin: EventOrigin.Toolbar });
                                }}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    px: "8px",
                                    py: "6px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    backgroundColor: isActive ? "#D2E0F9" : "transparent",
                                    "&:hover": {
                                        backgroundColor: isActive ? "#BCD1F5" : TOOLBAR_COLORS.HOVER,
                                    },
                                    userSelect: "none",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: TOOLBAR_COLORS.DEFAULT,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Icon />
                                </Box>
                                <Typography
                                    sx={{
                                        flex: 1,
                                        fontSize: 14,
                                        lineHeight: "20px",
                                        letterSpacing: "0.15px",
                                        color: "#232729",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {label}
                                </Typography>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        lineHeight: "16px",
                                        width: 24,
                                        py: "2px",
                                        borderRadius: "4px",
                                        backgroundColor: TOOLBAR_COLORS.TOOLTIP_PILL_BG,
                                        color: TOOLBAR_COLORS.TOOLTIP_TEXT,
                                        fontFamily: TOOLBAR_COLORS.TOOLTIP_FONT_FAMILY,
                                        flexShrink: 0,
                                        textAlign: "center",
                                    }}
                                >
                                    {shortcutKey}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </div>
        </Popper>
    );
}

const flyoutContainerStyle = {
    backgroundColor: TOOLBAR_CONTAINER.BG,
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
    padding: "4px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
} as const;

/** Suppresses column tooltips while an anchored popover is open. */
function FlyoutSuppressor({ open }: { open: boolean }) {
    useFlyoutRegistration(open);
    return null;
}

function BottomNavDivider() {
    return (
        <Box
            sx={{
                width: "1px",
                height: "20px",
                backgroundColor: "#D6DADC",
                flexShrink: 0,
                mx: "8px",
            }}
        />
    );
}

/**
 * Renders the managed bottom-nav buttons in order, drawing a divider for each
 * separator entry only when at least one button sits on each side of it.
 */
function BottomNavButtons() {
    const entries = useToolbarButtons(CORE_TOOLBAR_IDS.BOTTOM_NAV);

    return (
        <>
            {entries.map((entry, i) => {
                if (entry.config.separator) {
                    const hasButtonBefore = entries.slice(0, i).some((e) => !e.config.separator);
                    const hasButtonAfter = entries.slice(i + 1).some((e) => !e.config.separator);
                    if (!hasButtonBefore || !hasButtonAfter) {
                        return null;
                    }
                    return <BottomNavDivider key={entry.config.id} />;
                }
                return <ToolbarButton key={entry.config.id} entry={entry} />;
            })}
        </>
    );
}

export function BottomNavToolbar() {
    const dispatch = useAppDispatch();
    const {
        state: { view, scene, dataAccess },
    } = useExplorerGlobals(true);
    const dispatchToolbars = useDispatchToolbars();

    const controller = useAppSelector(selectCameraDefaults).pinhole.controller;
    const cameraType = useAppSelector(selectCameraType);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const isSubtreesPanelOpen = useAppSelector(selectIsSubtreesPanelOpen);
    const mainObject = useAppSelector(selectMainObject);
    const mainObjectPick = useAppSelector(selectMainObjectPick);
    const { showNavIcon: spaceMouseEnabled } = useSpaceMouseAccess();

    const setController = useSetController();
    const toggleXRayMode = useToggleXRayMode();
    const resetView = useResetView();
    const resetViewRef = useRef(resetView);
    resetViewRef.current = resetView;

    const [navModeOpen, setNavModeOpen] = useState(false);

    const navModeBtnRef = useRef<HTMLButtonElement>(null);
    const navModeFlyoutRef = useRef<HTMLDivElement>(null);
    const renderBtnRef = useRef<HTMLButtonElement>(null);
    const registeredRef = useRef(false);

    const availableButtons = useToolbarButtons(CORE_TOOLBAR_IDS.BOTTOM_NAV);
    const availableButtonsRef = useRef(availableButtons);
    availableButtonsRef.current = availableButtons;

    useEffect(() => {
        if (!navModeOpen) {
            return;
        }
        const handleMouseDown = (e: MouseEvent) => {
            if (
                navModeBtnRef.current?.contains(e.target as Node) ||
                navModeFlyoutRef.current?.contains(e.target as Node)
            ) {
                return;
            }
            setNavModeOpen(false);
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [navModeOpen]);

    const handleHome = useCallback(async () => {
        await resetViewRef.current();
        void track(Events.toolbar.homeTapped, { origin: EventOrigin.Toolbar });
    }, []);

    const handleNavModeToggle = useCallback(() => setNavModeOpen((o) => !o), []);

    const handleCameraToggle = useCallback(() => {
        if (!view || !scene) {
            return;
        }
        void track(Events.toolbar.cameraTapped, { origin: EventOrigin.Toolbar });
        if (cameraType === CameraType.Pinhole) {
            getOrthoFromPinhole(view, { dataAccess, up: scene?.up, mainObject })
                .then(async (orthographicParams) => {
                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: { ...orthographicParams, flyTime: 0 },
                        }),
                    );
                    // Reveal a deep selected object with a world-fixed clip plane in front of it.
                    const clipPlane = await getOrthoFitClipPlane({ view, dataAccess, mainObject, mainObjectPick });
                    if (clipPlane) {
                        dispatch(renderActions.addClippingPlane(clipPlane));
                    }
                })
                .catch((error) => {
                    console.warn("Failed to build orthographic camera:", error);
                    telemetry.trackCaughtException({ source: "BottomNavToolbar", category: "render", error });
                    dispatch(renderActions.setCamera({ type: CameraType.Orthographic }));
                });
        } else {
            dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
            dispatch(renderActions.removeTemporaryClippingPlanes());
        }
    }, [dispatch, view, scene, dataAccess, cameraType, mainObject, mainObjectPick]);

    const handleRenderToggle = useCallback(() => {
        dispatch(renderActions.setSubtreesPanelOpen(!isSubtreesPanelOpen));
    }, [dispatch, isSubtreesPanelOpen]);

    // Register the toolbar and its core buttons once. No cleanup, so SDK-added
    // custom buttons persist across re-renders.
    useEffect(() => {
        if (registeredRef.current) {
            return;
        }
        const isOrtho = cameraType === CameraType.Orthographic;
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "home",
                    icon: <HomeIcon sx={{ fontSize: TOOLBAR_BUTTON.ICON_SIZE }} />,
                    onClick: handleHome,
                    tooltip: {
                        toolName: "home",
                        label: "Home",
                        shortcutKey: isMacintosh ? "Cmd+0" : "Ctrl+0",
                        placement: "top",
                        showOnHover: true,
                    },
                },
                state: { active: false, disabled: false, visible: true },
            },
            {
                config: {
                    id: "navigationMode",
                    icon: <NavModeIcon controller={controller} spaceMouseEnabled={spaceMouseEnabled} />,
                    onClick: handleNavModeToggle,
                    ref: navModeBtnRef,
                    hasExpander: true,
                    expanderPosition: "bottom-right",
                    tooltip: {
                        toolName: "navigationMode",
                        label: "Navigation Mode",
                        shortcutKey: "",
                        placement: "top",
                        showOnHover: !navModeOpen,
                    },
                },
                state: { active: navModeOpen, disabled: false, visible: true },
            },
            {
                config: { id: "divider", separator: true },
                state: { active: false, disabled: false, visible: true },
            },
            {
                config: {
                    id: "camera",
                    icon: isOrtho ? <PerspectiveIcon /> : <OrthographicIcon />,
                    onClick: handleCameraToggle,
                    tooltip: {
                        toolName: isOrtho ? "perspective" : "orthographic",
                        shortcutKey: "",
                        placement: "top",
                        showOnHover: true,
                    },
                },
                state: { active: false, disabled: !view || !scene, visible: true },
            },
            {
                config: {
                    id: "renderModes",
                    icon: <LayersFill />,
                    onClick: handleRenderToggle,
                    ref: renderBtnRef,
                    hasExpander: true,
                    expanderPosition: "bottom-right",
                    tooltip: {
                        toolName: "renderModes",
                        shortcutKey: "",
                        placement: "top",
                        showOnHover: !isSubtreesPanelOpen,
                    },
                },
                state: { active: isSubtreesPanelOpen, disabled: false, visible: true },
            },
            {
                config: {
                    id: "xRay",
                    icon: <XRayIcon />,
                    onClick: toggleXRayMode,
                    tooltip: {
                        toolName: "xRay",
                        shortcutKey: isMacintosh ? "Cmd+X" : "Ctrl+X",
                        placement: "top",
                        showOnHover: true,
                    },
                },
                state: { active: isXRayActive, disabled: !view || !scene, visible: true },
            },
        ];
        dispatchToolbars(toolbarsActions.addToolbar({ id: CORE_TOOLBAR_IDS.BOTTOM_NAV, buttons }));
        registeredRef.current = true;
    }, [
        dispatchToolbars,
        handleHome,
        handleNavModeToggle,
        handleCameraToggle,
        handleRenderToggle,
        toggleXRayMode,
        cameraType,
        controller,
        spaceMouseEnabled,
        navModeOpen,
        isSubtreesPanelOpen,
        isXRayActive,
        view,
        scene,
    ]);

    // Rebind interactive handlers and anchor refs to the current component
    // instance once per mount. The toolbar registration above runs only once
    // and has no cleanup (to preserve SDK-added buttons), so `addToolbar` is a
    // no-op when this component unmounts and remounts — e.g. via
    // setToolbarVisible(false)/setToolbarVisible(true). Without this, the
    // persisted button configs keep the dead instance's onClick closures and
    // anchor refs, which breaks the nav-mode and render-mode popovers (their
    // open state and anchorEl would target the unmounted instance). The onClick
    // values are stable wrappers that read the latest handler from a ref, so
    // this effect runs a single time per mount and never loops.

    // Also suppress the per-button hover tooltips for the flyout buttons while their
    // flyout is open, matching the previous EnhancedTooltip behavior.
    useEffect(() => {
        if (!availableButtonsRef.current.some((b) => b.config.id === "home")) {
            return;
        }
        dispatchToolbars(
            toolbarsActions.updateButtonConfig(CORE_TOOLBAR_IDS.BOTTOM_NAV, "home", {
                onClick: handleHome,
            }),
        );
    }, [dispatchToolbars, handleHome]);

    useEffect(() => {
        if (!availableButtonsRef.current.some((b) => b.config.id === "navigationMode")) {
            return;
        }
        dispatchToolbars(
            toolbarsActions.updateButtonConfig(CORE_TOOLBAR_IDS.BOTTOM_NAV, "navigationMode", {
                onClick: handleNavModeToggle,
                ref: navModeBtnRef,
                icon: <NavModeIcon controller={controller} spaceMouseEnabled={spaceMouseEnabled} />,
                tooltip: {
                    toolName: "navigationMode",
                    label: "Navigation Mode",
                    shortcutKey: "",
                    placement: "top",
                    showOnHover: !navModeOpen,
                },
            }),
        );
    }, [dispatchToolbars, handleNavModeToggle, navModeOpen, controller, spaceMouseEnabled]);

    useEffect(() => {
        const isOrtho = cameraType === CameraType.Orthographic;
        if (!availableButtonsRef.current.some((b) => b.config.id === "camera")) {
            return;
        }
        dispatchToolbars(
            toolbarsActions.updateButtonConfig(CORE_TOOLBAR_IDS.BOTTOM_NAV, "camera", {
                onClick: handleCameraToggle,
                icon: isOrtho ? <PerspectiveIcon /> : <OrthographicIcon />,
                tooltip: {
                    toolName: isOrtho ? "perspective" : "orthographic",
                    shortcutKey: "",
                    placement: "top",
                    showOnHover: true,
                },
            }),
        );
    }, [dispatchToolbars, handleCameraToggle, cameraType]);

    useEffect(() => {
        if (!availableButtonsRef.current.some((b) => b.config.id === "renderModes")) {
            return;
        }
        dispatchToolbars(
            toolbarsActions.updateButtonConfig(CORE_TOOLBAR_IDS.BOTTOM_NAV, "renderModes", {
                onClick: handleRenderToggle,
                ref: renderBtnRef,
                tooltip: {
                    toolName: "renderModes",
                    shortcutKey: "",
                    placement: "top",
                    showOnHover: !isSubtreesPanelOpen,
                },
            }),
        );
    }, [dispatchToolbars, handleRenderToggle, isSubtreesPanelOpen]);

    useEffect(() => {
        if (!availableButtonsRef.current.some((b) => b.config.id === "xRay")) {
            return;
        }
        dispatchToolbars(
            toolbarsActions.updateButtonConfig(CORE_TOOLBAR_IDS.BOTTOM_NAV, "xRay", {
                onClick: toggleXRayMode,
            }),
        );
    }, [dispatchToolbars, toggleXRayMode]);

    // Sync button active/disabled state.
    useEffect(() => {
        const hasButton = (id: string) => availableButtonsRef.current.some((b) => b.config.id === id);
        dispatchToolbars(
            toolbarsActions.bulkUpdateButtonStates(CORE_TOOLBAR_IDS.BOTTOM_NAV, {
                ...(hasButton("navigationMode") && { navigationMode: { active: navModeOpen } }),
                ...(hasButton("camera") && { camera: { disabled: !view || !scene } }),
                ...(hasButton("renderModes") && { renderModes: { active: isSubtreesPanelOpen } }),
                ...(hasButton("xRay") && { xRay: { active: isXRayActive, disabled: !view || !scene } }),
            }),
        );
    }, [dispatchToolbars, navModeOpen, isSubtreesPanelOpen, isXRayActive, view, scene]);

    return (
        <Box
            sx={{
                position: "absolute",
                bottom: "16px",
                left: "50%",
                transform: "translateX(-50%)",
                pointerEvents: "none",
                zIndex: TOOLBAR_Z_INDEX.TOOLBAR,
            }}
        >
            <ToolbarLayerProvider>
                <Box sx={containerStyle}>
                    <FlyoutSuppressor open={navModeOpen || isSubtreesPanelOpen} />
                    <BottomNavButtons />
                </Box>
            </ToolbarLayerProvider>

            <NavModesPopover
                open={navModeOpen}
                anchorEl={navModeBtnRef.current}
                flyoutRef={navModeFlyoutRef}
                controller={controller}
                spaceMouseEnabled={spaceMouseEnabled}
                setController={setController}
                onClose={() => setNavModeOpen(false)}
            />

            <SubtreesPopover
                open={isSubtreesPanelOpen}
                anchorEl={renderBtnRef.current}
                onClose={() => dispatch(renderActions.setSubtreesPanelOpen(false))}
                anchorOrigin={{ vertical: -6, horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                paperBorderRadius="8px"
            />
        </Box>
    );
}
